// NOTE: Backend API endpoint /api/admin/payout-config needs to be created in merchant-service
// It should support GET (list all), POST (create/update), and DELETE (by merchantId)
// See: backend/merchant-service/

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPayoutConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    merchantId: '', aggrId: '', aggrName: '', corpId: '',
    userId: '', urn: '', debitAcc: '', workflowReqd: 'Y',
    preferredProvider: 'ICICI_CIB', perTransactionLimit: '',
    dailyLimit: '', payoutApiKey: '', payoutApiUrl: '', payoutPublicKeyName: ''
  });

  const headers = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try {
      const res = await axios.get('/api/admin/payout-config', { headers: headers(), withCredentials: true });
      if (res.data.success) setConfigs(res.data.data || []);
    } catch (e) { console.error('Failed to fetch payout configs:', e); }
    finally { setLoading(false); }
  };

  const openEdit = (config) => {
    setForm({
      merchantId: config.merchantId || '',
      aggrId: config.aggrId || '',
      aggrName: config.aggrName || '',
      corpId: config.corpId || '',
      userId: config.userId || '',
      urn: config.urn || '',
      debitAcc: config.debitAcc || '',
      workflowReqd: config.workflowReqd || 'Y',
      preferredProvider: config.preferredProvider || 'ICICI_CIB',
      perTransactionLimit: config.perTransactionLimit || '',
      dailyLimit: config.dailyLimit || '',
      payoutApiKey: config.payoutApiKey || '',
      payoutApiUrl: config.payoutApiUrl || '',
      payoutPublicKeyName: config.payoutPublicKeyName || ''
    });
    setEditingId(config.id);
  };

  const resetForm = () => {
    setForm({
      merchantId: '', aggrId: '', aggrName: '', corpId: '',
      userId: '', urn: '', debitAcc: '', workflowReqd: 'Y',
      preferredProvider: 'ICICI_CIB', perTransactionLimit: '',
      dailyLimit: '', payoutApiKey: '', payoutApiUrl: '', payoutPublicKeyName: ''
    });
    setEditingId(null);
  };

  const saveConfig = async () => {
    if (!form.merchantId.trim()) { alert('Merchant ID is required'); return; }
    if (!form.aggrId.trim()) { alert('AGGRID (Aggregator ID) is required'); return; }
    if (!form.corpId.trim()) { alert('CORPID (Corporate ID) is required'); return; }
    if (!form.userId.trim()) { alert('USERID (User ID) is required'); return; }
    if (!form.urn.trim()) { alert('URN is required'); return; }
    if (!form.debitAcc.trim()) { alert('Debit Account (DEBITACC) is required'); return; }
    if (!form.payoutPublicKeyName.trim()) { alert('Payout Public Key Name is required'); return; }
    if (!form.payoutApiKey.trim()) { alert('Payout API Key is required'); return; }
    if (!form.payoutApiUrl.trim()) { alert('Payout API URL is required'); return; }
    setSaving(true);
    try {
      await axios.post('/api/admin/payout-config', form, { headers: { ...headers(), 'Content-Type': 'application/json' }, withCredentials: true });
      resetForm();
      fetchConfigs();
    } catch (e) {
      alert('Failed to save: ' + (e.response?.data?.message || e.message));
    } finally { setSaving(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }} role="status" aria-live="polite">Loading...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Payout Configuration (ICICI CIB)</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => window.location.href = '/admin'}
            style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            ← Back to Dashboard
          </button>
          <button onClick={() => { setEditingId('NEW'); resetForm(); setEditingId('NEW'); }}
            style={{ padding: '10px 20px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
            + Add Merchant Payout Config
          </button>
        </div>
      </div>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Configure per-merchant payout settings for ICICI CIB integration. Each merchant requires its own
        Aggregator ID, Corporate ID, URN, and debit account for payout processing.
      </p>

      {/* Edit Form */}
      {editingId && (
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>{editingId === 'NEW' ? 'Add Merchant Payout Config' : `Edit Payout Config`}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Merchant ID *</label>
              <input type="text" value={form.merchantId} onChange={e => setForm({...form, merchantId: e.target.value})}
                placeholder="e.g. MER00123" style={inputStyle} disabled={editingId !== 'NEW'} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Preferred Bank Provider</label>
              <select value={form.preferredProvider} onChange={e => setForm({...form, preferredProvider: e.target.value})} style={inputStyle}>
                <option value="ICICI_CIB">ICICI CIB</option>
                <option value="ICICI_IMPS">ICICI IMPS</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #b8daff' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#004085' }}>ICICI CIB Credentials</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>AGGRID (Aggregator ID) *</label>
                <input type="text" value={form.aggrId} onChange={e => setForm({...form, aggrId: e.target.value})}
                  placeholder="Aggregator ID" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>AGGRNAME (Aggregator Name)</label>
                <input type="text" value={form.aggrName} onChange={e => setForm({...form, aggrName: e.target.value})}
                  placeholder="Aggregator Name" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>CORPID (Corporate ID) *</label>
                <input type="text" value={form.corpId} onChange={e => setForm({...form, corpId: e.target.value})}
                  placeholder="Corporate ID" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>USERID (User ID) *</label>
                <input type="text" value={form.userId} onChange={e => setForm({...form, userId: e.target.value})}
                  placeholder="User ID" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>URN *</label>
                <input type="text" value={form.urn} onChange={e => setForm({...form, urn: e.target.value})}
                  placeholder="URN" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Debit Account (DEBITACC) *</label>
                <input type="text" value={form.debitAcc} onChange={e => setForm({...form, debitAcc: e.target.value})}
                  placeholder="Debit Account Number" style={inputStyle} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#856404' }}>API Credentials *</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout API Key *</label>
                <input type="password" value={form.payoutApiKey} onChange={e => setForm({...form, payoutApiKey: e.target.value})}
                  placeholder="ICICI CIB API Key" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout API URL *</label>
                <input type="text" value={form.payoutApiUrl} onChange={e => setForm({...form, payoutApiUrl: e.target.value})}
                  placeholder="e.g. https://apibanking.icicibank.com/api/Corporate/CIB_SV/v1/Transaction" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Payout Public Key Name *</label>
                <input type="text" value={form.payoutPublicKeyName} onChange={e => setForm({...form, payoutPublicKeyName: e.target.value})}
                  placeholder="e.g. icici_payout_public.pem" style={inputStyle} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '16px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Limits & Workflow</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>WORKFLOW_REQD</label>
                <select value={form.workflowReqd} onChange={e => setForm({...form, workflowReqd: e.target.value})} style={inputStyle}>
                  <option value="Y">Y (Yes)</option>
                  <option value="N">N (No)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Per-Transaction Limit (₹)</label>
                <input type="number" value={form.perTransactionLimit} onChange={e => setForm({...form, perTransactionLimit: e.target.value})}
                  placeholder="e.g. 200000" style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Daily Limit (₹)</label>
                <input type="number" value={form.dailyLimit} onChange={e => setForm({...form, dailyLimit: e.target.value})}
                  placeholder="e.g. 5000000" style={inputStyle} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={resetForm} style={{ padding: '10px 24px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
            <button onClick={saveConfig} disabled={saving}
              style={{ padding: '10px 24px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}

      {/* Configs Table */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} role="table">
          <caption className="sr-only">Merchant Payout Configurations</caption>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
              <th scope="col" style={thStyle}>Merchant ID</th>
              <th scope="col" style={thStyle}>AGGRID</th>
              <th scope="col" style={thStyle}>CORPID</th>
              <th scope="col" style={thStyle}>Provider</th>
              <th scope="col" style={thStyle}>Txn Limit</th>
              <th scope="col" style={thStyle}>Daily Limit</th>
              <th scope="col" style={thStyle}>Workflow</th>
              <th scope="col" style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#767676' }}>No payout configs. Click "+ Add Merchant Payout Config" to create one.</td></tr>
            ) : configs.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}><strong>{c.merchantId}</strong></td>
                <td style={tdStyle}>{c.aggrId}</td>
                <td style={tdStyle}>{c.corpId}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    background: c.preferredProvider === 'ICICI_CIB' ? '#cce5ff' : '#d4edda',
                    color: c.preferredProvider === 'ICICI_CIB' ? '#004085' : '#155724' }}>
                    {c.preferredProvider}
                  </span>
                </td>
                <td style={tdStyle}>₹{c.perTransactionLimit ? Number(c.perTransactionLimit).toLocaleString() : '—'}</td>
                <td style={tdStyle}>₹{c.dailyLimit ? Number(c.dailyLimit).toLocaleString() : '—'}</td>
                <td style={tdStyle}>
                  <span style={{ padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                    background: c.workflowReqd === 'Y' ? '#fff3cd' : '#d4edda',
                    color: c.workflowReqd === 'Y' ? '#856404' : '#155724' }}>
                    {c.workflowReqd}
                  </span>
                </td>
                <td style={tdStyle}>
                  <button onClick={() => openEdit(c)} style={{ padding: '5px 12px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' };
const tdStyle = { padding: '12px 16px', verticalAlign: 'middle' };

export default AdminPayoutConfig;
